import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import Lenis from "lenis";
import { LanguageProvider } from "@/lib/i18n";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import WhatsAppFab from "@/components/site/WhatsAppFab";
import Home from "@/pages/Home";
import Programmes from "@/pages/Programmes";
import Ebook from "@/pages/Ebook";
import Temoignages from "@/pages/Temoignages";
import APropos from "@/pages/APropos";
import Faq from "@/pages/Faq";
import Contact from "@/pages/Contact";
import Admin from "@/pages/Admin";

const ScrollToTop = () => {
    const { pathname } = useLocation();
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "instant" });
    }, [pathname]);
    return null;
};

const AuthCallback = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const hasProcessed = useRef(false);

    useEffect(() => {
        if (hasProcessed.current) return;
        hasProcessed.current = true;
        const sessionId = location.hash.split("session_id=")[1]?.split("&")[0];
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/session`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ session_id: sessionId }),
        })
            .then((r) => (r.ok ? r.json() : Promise.reject()))
            .then((user) => navigate("/admin", { state: { user }, replace: true }))
            .catch(() => navigate("/admin", { replace: true }));
    }, [location.hash, navigate]);

    return null;
};

const useLenis = () => {
    useEffect(() => {
        const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
        const raf = (time) => {
            lenis.raf(time);
            requestAnimationFrame(raf);
        };
        const id = requestAnimationFrame(raf);
        return () => {
            cancelAnimationFrame(id);
            lenis.destroy();
        };
    }, []);
};

const Shell = () => {
    useLenis();
    const location = useLocation();
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }
    return (
        <>
            <ScrollToTop />
            <Navbar />
            <main className="grain">
                <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/programmes" element={<Programmes />} />
                    <Route path="/ebook" element={<Ebook />} />
                    <Route path="/temoignages" element={<Temoignages />} />
                    <Route path="/a-propos" element={<APropos />} />
                    <Route path="/faq" element={<Faq />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/admin" element={<Admin />} />
                    <Route path="*" element={<Home />} />
                </Routes>
            </main>
            <Footer />
            <WhatsAppFab />
        </>
    );
};

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <LanguageProvider>
                    <Shell />
                </LanguageProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
